import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Typography,
  Paper,
  CircularProgress,
  IconButton,
  Tooltip,
  InputAdornment,
  TextField,
} from "@mui/material";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import { commonRunQuery } from "../../util/API/loans";
import BaseCustomerTable from "../../components/BaseCustomerTable/BaseCustomerTable";

// ── Empty state ──
const EmptyStateRow = ({ colSpan, message }) => (
  <TableRow>
    <TableCell colSpan={colSpan} align="center" sx={{ py: 5 }}>
      <Typography variant="body2" color="textSecondary">
        {message || "No results found."}
      </Typography>
    </TableCell>
  </TableRow>
);

// ── Type badge config ──
const TYPE_CONFIG = {
  Fees:                  { bg: "#F1EFE8", color: "#5F5E5A", border: "#B4B2A9" },
  Purchase:              { bg: "#FCEBEB", color: "#A32D2D", border: "#F09595" },
  Sale:                  { bg: "#EAF3DE", color: "#3B6D11", border: "#97C459" },
  "Agent Deposit":       { bg: "#E1F5EE", color: "#0F6E56", border: "#5DCAA5" },
  "Customer Withdrawal": { bg: "#FAEEDA", color: "#854F0B", border: "#EF9F27" },
  "NCS ATM Withdrawal":  { bg: "#FCEBEB", color: "#A32D2D", border: "#F09595" },
  "NCS ATM Deposit":     { bg: "#EAF3DE", color: "#3B6D11", border: "#97C459" },
};

const FILTER_STYLES = {
  all:                   { bg: "#f0f0f0",  color: "#444",    border: "#ccc" },
  Fees:                  { bg: "#F1EFE8",  color: "#5F5E5A", border: "#B4B2A9" },
  Purchase:              { bg: "#FCEBEB",  color: "#A32D2D", border: "#F09595" },
  Sale:                  { bg: "#EAF3DE",  color: "#3B6D11", border: "#97C459" },
  "Agent Deposit":       { bg: "#E1F5EE",  color: "#0F6E56", border: "#5DCAA5" },
  "Customer Withdrawal": { bg: "#FAEEDA",  color: "#854F0B", border: "#EF9F27" },
  "NCS ATM Withdrawal":  { bg: "#FCEBEB",  color: "#A32D2D", border: "#F09595" },
  "NCS ATM Deposit":     { bg: "#EAF3DE",  color: "#3B6D11", border: "#97C459" },
};

// ── Type Badge ──
const TypeBadge = ({ type }) => {
  const cfg = TYPE_CONFIG[type] || { bg: "#f5f5f5", color: "#666", border: "#ddd" };
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        px: "10px",
        py: "3px",
        borderRadius: "20px",
        fontSize: "11px",
        fontWeight: 600,
        letterSpacing: "0.02em",
        background: cfg.bg,
        color: cfg.color,
        border: `0.5px solid ${cfg.border}`,
        whiteSpace: "nowrap",
      }}
    >
      {type}
    </Box>
  );
};

// ── Amount cell ──
const AmountCell = ({ value }) => {
  if (value === "—")
    return (
      <Typography className="email-admin-text" sx={{ color: "#bbb", textAlign: "right" }}>
        —
      </Typography>
    );
  const num = parseFloat(value);
  if (isNaN(num))
    return (
      <Typography className="email-admin-text" sx={{ textAlign: "right" }}>
        {value}
      </Typography>
    );
  return (
    <Typography
      className="email-admin-text"
      sx={{
        fontWeight: 500,
        textAlign: "right",
        fontFamily: "monospace",
        color: num >= 0 ? "#2e7d32" : "#d32f2f",
      }}
    >
      {num >= 0 ? "+" : ""}
      {num.toLocaleString("en", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
    </Typography>
  );
};

// ── Main Component ──
const TransactionHistory = () => {
  const [rows, setRows]                 = useState([]);
  const [loading, setLoading]           = useState(true);
  const [search, setSearch]             = useState("");
  const [showSearch, setShowSearch]     = useState(false);
  const [activeFilter, setActiveFilter] = useState("all");
  const [page, setPage]                 = useState(0);
  const [rowsPerPage, setRowsPerPage]   = useState(10);
  const [totalCount, setTotalCount]     = useState(0);
  const [sortDir, setSortDir]           = useState("desc");

  useEffect(() => {
    fetchTransactions(page, rowsPerPage);
  }, [page, rowsPerPage]);

  const fetchTransactions = async (currentPage = 0, limit = 10) => {
    setLoading(true);
    const offset = currentPage * limit;
    try {
      const response = await commonRunQuery(380, `${limit}/${offset}`);
      const header   = response?.data?.header;
      const body     = response?.data?.body;
      const record   = body?.record;
      const reccount = Number(header?.reccount ?? 0);

      if (record) {
        const arr    = Array.isArray(record) ? record : [record];
        const mapped = arr.map((item) => {
          const t = item?.vtranshistory ?? item;
          return {
            account_id:       t?.acctID            ?? "—",
            currency:         t?.currID            ?? "—",
            amount:           t?.amount            ?? "—",
            trans_time:       t?.transTime         ?? "—",
            transaction_type: t?.transtypelabel    ?? "—",
            source_user:      t?.sourceUserID      ?? "—",
            destination_user: t?.destinationUserID ?? "—",
            txid:             t?.referenceTXID     ?? "—",
            trans_line_id:    t?.transLineID       ?? "—",
            balance:          t?.balance           ?? "—",
            batch_request_id: t?.batchrequestid    ?? "0",
            channelid:        t?.channelid         ?? "0",
            channelname:      t?.channelname       ?? "Not Available",
            source_node:      t?.sourceNodeID      ?? "—",
            destination_node: t?.destinationNodeID ?? "—",
            trans_id:         t?.transID           ?? "—",
            trans_type:       t?.transType         ?? "—",
          };
        });
        setRows(mapped);
        setTotalCount(
          reccount === limit ? offset + reccount + 1 : offset + reccount
        );
      } else {
        setRows([]);
        setTotalCount(0);
      }
    } catch (err) {
      console.error("Error fetching transactions:", err);
    } finally {
      setLoading(false);
    }
  };

  // ── Client-side filter + search ──
  const filtered = useMemo(() => {
    let data = [...rows];
    if (activeFilter !== "all")
      data = data.filter((r) => r.transaction_type === activeFilter);
    if (search.trim()) {
      const q = search.toLowerCase();
      data = data.filter((r) =>
        [r.account_id, r.txid, r.source_user, r.destination_user, r.channelname]
          .join(" ")
          .toLowerCase()
          .includes(q)
      );
    }
    // Fix — proper datetime sort
data.sort((a, b) => {
  const ta = new Date(a.trans_time ?? 0).getTime();
  const tb = new Date(b.trans_time ?? 0).getTime();
  return sortDir === "desc" ? tb - ta : ta - tb;
});
    return data;
  }, [rows, activeFilter, search, sortDir]);

  // Server-side pagination — page changes trigger a new API fetch
  const pageCount   = Math.ceil(totalCount / rowsPerPage);
  const pageNumbers = Array.from({ length: pageCount }, (_, i) => i);

  const handleChangePage = useCallback((_, newPage) => {
    setPage(newPage);
    fetchTransactions(newPage, rowsPerPage);
  }, [rowsPerPage]);

  const handleFilter = useCallback((type) => { setActiveFilter(type); }, []);
  const handleSearch = useCallback((e) => { setSearch(e.target.value); }, []);
  const toggleSort              = () => setSortDir((d) => (d === "desc" ? "asc" : "desc"));

  const [exporting, setExporting] = useState(false);

  const exportCSV = async () => {
    setExporting(true);
    try {
      // Fetch ALL records in one go using totalCount as the limit
      const fetchLimit = totalCount > 0 ? totalCount : 10000;
      const response   = await commonRunQuery(380, `${fetchLimit}/0`);
      const record     = response?.data?.body?.record;
      const arr        = record ? (Array.isArray(record) ? record : [record]) : [];

      const allRows = arr.map((item) => {
        const t = item?.vtranshistory ?? item;
        return {
          account_id:       t?.acctID            ?? "",
          trans_time:       t?.transTime         ?? "",
          transaction_type: t?.transtypelabel    ?? "",
          source_user:      t?.sourceUserID      ?? "",
          destination_user: t?.destinationUserID ?? "",
          amount:           t?.amount            ?? "",
          balance:          t?.balance           ?? "",
          txid:             t?.referenceTXID     ?? "",
          channelname:      t?.channelname       ?? "",
        };
      });

      const headers = ["Account ID", "Trans. Time", "Type", "Source User", "Dest. User", "Amount", "Balance", "TXID", "Channel"];
      const keys    = ["account_id", "trans_time", "transaction_type", "source_user", "destination_user", "amount", "balance", "txid", "channelname"];

      // Wrap values in quotes to handle commas inside fields
      const escape  = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
      const csvRows = [
        headers.map(escape).join(","),
        ...allRows.map((r) => keys.map((k) => escape(r[k])).join(",")),
      ];

      const blob = new Blob([csvRows.join("\n")], { type: "text/csv;charset=utf-8;" });
      Object.assign(document.createElement("a"), {
        href: URL.createObjectURL(blob),
        download: `transactions_${new Date().toISOString().slice(0, 10)}.csv`,
      }).click();
    } catch (err) {
      console.error("Export failed:", err);
    } finally {
      setExporting(false);
    }
  };

  const filterOptions = [
    { key: "all", label: "All" },
    ...Object.keys(TYPE_CONFIG).map((k) => ({ key: k, label: k })),
  ];

  // Matches PayrollAdmin header styles
  const thSx = {
    fontFamily: "Inter, sans-serif",
    fontWeight: 600,
    fontSize: "13px",
    color: "#8A92A0",
    backgroundColor: "#fff",
    borderBottom: "1px solid #EAEEF4",
    whiteSpace: "nowrap",
    padding: "12px 16px",
  };

  return (
    <Box className="page-container admin-container">
      {/* ── Page Header — matches PayrollAdmin layout ── */}
      <Box
        className="header-container"
        style={{ display: "flex", justifyContent: "space-between", marginBottom: 16 }}
      >
        <Box style={{ display: "flex", alignItems: "center" }}>
          <Box className="admin-header">Transactions Journal</Box>
          <Box className="admin-subtitle" style={{ marginLeft: 10 }}>
            {loading ? "Loading…" : `${filtered.length} records on this page`}
          </Box>
        </Box>

        {/* Right-side controls */}
        <Box display="flex" gap={1} alignItems="center">
          {/* Filter chips */}
          <Box display="flex" gap={0.75} sx={{ flexWrap: "wrap", alignItems: "center" }}>
            {filterOptions.map(({ key, label }) => {
              const isActive = activeFilter === key;
              const st = FILTER_STYLES[key] || {};
              return (
                <Box
                  key={key}
                  component="span"
                  onClick={() => handleFilter(key)}
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    px: "10px",
                    py: "3px",
                    borderRadius: "20px",
                    fontSize: "11px",
                    fontWeight: 500,
                    cursor: "pointer",
                    transition: "all .15s",
                    border: isActive
                      ? `0.5px solid ${st.border}`
                      : "0.5px solid #EAEEF4",
                    background: isActive ? st.bg : "#fff",
                    color: isActive ? st.color : "#8A92A0",
                    "&:hover": { background: isActive ? st.bg : "#f5f7fa" },
                  }}
                >
                  {label}
                </Box>
              );
            })}
          </Box>

          {/* Search toggle */}
          <Tooltip title="Search">
            <IconButton
              size="small"
              onClick={() => setShowSearch((s) => !s)}
              sx={{
                border: "1px solid #EAEEF4",
                borderRadius: "8px",
                color: showSearch ? "#00365A" : "#8A92A0",
                bgcolor: showSearch ? "#e8f4ff" : "#fff",
                "&:hover": { bgcolor: "#f5f7fa" },
              }}
            >
              <SearchIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          {/* Export CSV */}
          <Tooltip title={exporting ? "Fetching all records…" : "Export all to CSV"}>
            <span>
              <IconButton
                size="small"
                onClick={exportCSV}
                disabled={exporting}
                sx={{
                  border: "1px solid #EAEEF4",
                  borderRadius: "8px",
                  color: exporting ? "#ccc" : "#8A92A0",
                  bgcolor: "#fff",
                  "&:hover": { bgcolor: "#f5f7fa" },
                }}
              >
                {exporting ? <CircularProgress size={16} thickness={3} /> : <FileDownloadIcon fontSize="small" />}
              </IconButton>
            </span>
          </Tooltip>

          {/* Refresh */}
          <Tooltip title="Refresh">
            <IconButton
              size="small"
              onClick={() => fetchTransactions(page, rowsPerPage)}
              sx={{
                border: "1px solid #EAEEF4",
                borderRadius: "8px",
                color: "#8A92A0",
                bgcolor: "#fff",
                "&:hover": { bgcolor: "#f5f7fa" },
              }}
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* ── Search bar ── */}
      {showSearch && (
        <TextField
          size="small"
          fullWidth
          placeholder="Search by account ID, TXID, user…"
          value={search}
          onChange={handleSearch}
          sx={{
            mb: 1.5,
            "& .MuiOutlinedInput-root": {
              fontSize: "13px",
              height: "36px",
              borderRadius: "8px",
              backgroundColor: "#fafafa",
              "& fieldset": { borderColor: "#EAEEF4" },
              "&:hover fieldset": { borderColor: "#bbb" },
              "&.Mui-focused fieldset": { borderColor: "#00365A", borderWidth: "1px" },
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 15, color: "#bbb" }} />
              </InputAdornment>
            ),
          }}
        />
      )}

      {/* ── Table — matches PayrollAdminTable structure ── */}
      <Box className="admin-table-container">
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer component={Paper} sx={{ boxShadow: "none" }}>
              <Table sx={{ minWidth: 700 }} aria-label="transactions journal table">
                <TableHead>
                  <TableRow>
                    <TableCell sx={thSx}>
                      <Typography className="customer-table-headers">Account ID</Typography>
                    </TableCell>
                    <TableCell
                      sx={{ ...thSx, cursor: "pointer" }}
                      onClick={toggleSort}
                    >
                      <Typography className="customer-table-headers">
                        Trans. Time&nbsp;
                        <span style={{ opacity: 0.5, fontSize: 11 }}>
                          {sortDir === "desc" ? "↓" : "↑"}
                        </span>
                      </Typography>
                    </TableCell>
                    <TableCell sx={thSx}>
                      <Typography className="customer-table-headers">Type</Typography>
                    </TableCell>
                    <TableCell sx={thSx}>
                      <Typography className="customer-table-headers">Source User</Typography>
                    </TableCell>
                    <TableCell sx={thSx}>
                      <Typography className="customer-table-headers">Dest. User</Typography>
                    </TableCell>
                    <TableCell sx={{ ...thSx, textAlign: "right" }}>
                      <Typography className="customer-table-headers">Amount</Typography>
                    </TableCell>
                    <TableCell sx={{ ...thSx, textAlign: "right" }}>
                      <Typography className="customer-table-headers">Balance</Typography>
                    </TableCell>
                    <TableCell sx={thSx}>
                      <Typography className="customer-table-headers">TXID</Typography>
                    </TableCell>
                    <TableCell sx={thSx}>
                      <Typography className="customer-table-headers">Channel</Typography>
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {filtered.length === 0 ? (
                    <EmptyStateRow colSpan={9} message="No transactions found." />
                  ) : (
                    filtered.map((row, idx) => (
                      <TableRow
                        key={idx}
                        hover
                        sx={{ borderBottom: "0.5px solid #f5f5f5" }}
                      >
                        <TableCell style={{ width: 120 }} align="left">
                          <Typography className="email-admin-text" sx={{ fontWeight: 500 }}>
                            {row.account_id}
                          </Typography>
                        </TableCell>
                        <TableCell style={{ width: 180 }} align="left">
                          <Typography className="email-admin-text" sx={{ color: "#8A92A0", whiteSpace: "nowrap" }}>
                            {row.trans_time}
                          </Typography>
                        </TableCell>
                        <TableCell style={{ width: 160 }} align="left">
                          <TypeBadge type={row.transaction_type} />
                        </TableCell>
                        <TableCell style={{ width: 130 }} align="left">
                          <Typography className="email-admin-text">
                            {row.source_user}
                          </Typography>
                        </TableCell>
                        <TableCell style={{ width: 130 }} align="left">
                          <Typography className="email-admin-text" sx={{ color: "#666" }}>
                            {row.destination_user}
                          </Typography>
                        </TableCell>
                        <TableCell style={{ width: 120 }} align="right">
                          <AmountCell value={row.amount} />
                        </TableCell>
                        <TableCell style={{ width: 120 }} align="right">
                          <Typography
                            className="email-admin-text"
                            sx={{ fontFamily: "monospace", textAlign: "right", color: "#555" }}
                          >
                            {row.balance === "—"
                              ? "—"
                              : isNaN(parseFloat(row.balance))
                              ? row.balance
                              : parseFloat(row.balance).toLocaleString("en", {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                })}
                          </Typography>
                        </TableCell>
                        <TableCell style={{ width: 140 }} align="left">
                          <Typography
                            className="email-admin-text"
                            sx={{ fontFamily: "monospace", color: "#1565c0" }}
                          >
                            {row.txid}
                          </Typography>
                        </TableCell>
                        <TableCell style={{ width: 140 }} align="left">
                          <Typography className="email-admin-text" sx={{ color: "#aaa" }}>
                            {row.channelname}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>

              {/* Reuse the same BaseCustomerTable pagination as PayrollAdminTable */}
              <BaseCustomerTable
                page={page}
                onPageChange={handleChangePage}
                pageNumbers={pageNumbers}
              />
            </TableContainer>
          </>
        )}
      </Box>
    </Box>
  );
};

export default TransactionHistory;
